import { Lead, Project, Category } from '../types';
import { ContextMenuItem } from '../components/ui/ContextMenu';
import { MessageSquare, Calendar, Briefcase, Plus, ExternalLink, XCircle, Ban, ArrowRight } from 'lucide-react';
import { appendEditDeleteMenuItems } from '../components/ui/tableActions';
import { getNextLeadStatus, isAutomaticLeadStatus, LEAD_STATUS_I18N_KEYS } from './statusUtils';
import { getLeadContactOpenAction } from './contactOutbound';

export type LeadActionsContext = 'table' | 'followup';

/** Handlers for the lead "more actions" menu. Passed from Leads to LeadTable and FollowUpList. */
export interface LeadMenuActions {
  onMessage?: (lead: Lead) => void;
  onContact?: (lead: Lead) => void;
  onReschedule?: (lead: Lead) => void;
  onCreateProject?: (lead: Lead) => void;
  onViewProject?: (project: Project) => void;
  onMarkAsAbandoned?: (lead: Lead) => void;
  onMarkAsRejected?: (lead: Lead) => void;
  onMoveToNextStatus?: (lead: Lead) => void;
}

export interface LeadActionsConfig extends LeadMenuActions {
  lead: Lead;
  projects?: Project[];
  contactCategory?: Category | null;
  t: (key: string) => string;
  /** Context determines action order: 'table' (default) or 'followup' */
  context?: LeadActionsContext;
  /** Force “Créer un projet” at the top (guided flow from lead modal). */
  preferCreateProject?: boolean;
  /** Edit/delete handlers — in ⋯ menu unless `omitEditDeleteFromMenu` (inline quick actions visible). */
  onEdit?: (lead: Lead) => void;
  onDelete?: (lead: Lead) => void;
  /** Edit/delete shown as row quick actions — omit duplicates from ⋯ menu. */
  omitEditDeleteFromMenu?: boolean;
}

/**
 * Génère les items du menu d'actions pour un lead
 * Utilisé à la fois dans LeadTable et FollowUpList pour éviter la duplication
 * 
 * Order depends on context:
 * - 'table': Move to next status > Message > Open contact (Gmail/profile/site) > Reschedule > Projects > Abandon > Refuse
 * - 'followup': Message > Open contact > Move to next status > Projects > Abandon > Refuse
 *   (Reschedule is a row quick action in FollowUpList, not duplicated in the ⋯ menu)
 */
export const getLeadActionMenuItems = (config: LeadActionsConfig): ContextMenuItem[] => {
  const {
    lead,
    projects = [],
    contactCategory,
    t,
    context = 'table',
    preferCreateProject = false,
    onMessage,
    onContact,
    onReschedule,
    onCreateProject,
    onViewProject,
    onMarkAsAbandoned,
    onMarkAsRejected,
    onMoveToNextStatus,
    onEdit,
    onDelete,
    omitEditDeleteFromMenu = false,
  } = config;

  const leadProjects = projects.filter((p) => p.leadId === lead.id);
  const projectCount = leadProjects.length;

  // Build individual action items (move to next status, or "Créer un projet" when next is in_production)
  const moveToNextStatusItem = (): ContextMenuItem | null => {
    if (isAutomaticLeadStatus(lead)) return null;
    const nextStatus = getNextLeadStatus(lead.status);
    if (!nextStatus) return null;

    // Quand le prochain statut serait "en production", proposer "Créer un projet" (le lead passera en prod à la création)
    if (nextStatus === 'in_production' && onCreateProject) {
      return {
        label: t('projects.createProject'),
        onClick: () => onCreateProject(lead),
        icon: ArrowRight,
      };
    }
    if (nextStatus === 'in_production') return null; // pas de "Passer à En production" sans action créer projet

    if (!onMoveToNextStatus) return null;
    const nextStatusLabel = t(LEAD_STATUS_I18N_KEYS[nextStatus] || `status.${nextStatus}`);
    return {
      label: `${t('leads.moveTo')} ${nextStatusLabel}`,
      onClick: () => onMoveToNextStatus(lead),
      icon: ArrowRight,
    };
  };

  const messageItem = (): ContextMenuItem | null => {
    const messageHandler = onMessage || onContact;
    if (!messageHandler) return null;
    return {
      label: t('leads.sendMessage'),
      onClick: () => messageHandler(lead),
      icon: MessageSquare,
    };
  };

  const openContactItem = (): ContextMenuItem | null => {
    const action = getLeadContactOpenAction(contactCategory, lead.contact_value, t);
    if (!action) return null;
    return {
      label: action.label,
      onClick: () => window.open(action.url, '_blank', 'noopener,noreferrer'),
      icon: ExternalLink,
    };
  };

  const pushMessageAndOpenContact = () => {
    const message = messageItem();
    const openContact = openContactItem();
    if (message) items.push(message);
    if (openContact) items.push(openContact);
  };

  const rescheduleItem = (): ContextMenuItem | null => {
    if (!onReschedule) return null;
    return {
      label: t('leads.rescheduleFollowUp'),
      onClick: () => onReschedule(lead),
      icon: Calendar,
    };
  };

  const projectsItem = (): ContextMenuItem => ({
    label: t('projects.manageProjects'),
    icon: Briefcase,
    badgeCount: projectCount,
    children: [
      ...(onCreateProject
        ? [{
            label: t('projects.newProject'),
            onClick: () => onCreateProject(lead),
            icon: Plus,
          }]
        : []),
      ...leadProjects.map((p) => ({
        label: p.name || t('projects.project'),
        onClick: () => {
          if (onViewProject) {
            onViewProject(p);
          } else {
            window.location.href = `/app/projects?project=${p.id}`;
          }
        },
        icon: ExternalLink,
      })),
    ],
  });

  const status = String(lead?.status ?? '').toLowerCase().trim();

  const abandonItem = (): ContextMenuItem | null => {
    if (!onMarkAsAbandoned) return null;
    if (status === 'abandoned') return null;
    return {
      label: t('leads.markAsAbandoned'),
      onClick: () => onMarkAsAbandoned(lead),
      icon: XCircle,
    };
  };

  const rejectItem = (): ContextMenuItem | null => {
    if (!onMarkAsRejected) return null;
    if (status === 'rejected') return null;
    return {
      label: t('leads.markAsRejected'),
      onClick: () => onMarkAsRejected(lead),
      icon: Ban,
    };
  };

  // Build items array based on context
  const items: ContextMenuItem[] = [];
  const isInProduction = status === 'in_production';
  const projectsFirst = isInProduction && projectCount > 0;

  const createProjectItem = (): ContextMenuItem | null => {
    if (!onCreateProject) return null;
    return {
      label: t('projects.createProject'),
      onClick: () => onCreateProject(lead),
      icon: Plus,
    };
  };

  if (context === 'followup') {
    // Followup context: prioritize followup-related actions (ou Gérer les projets en premier si en production).
    // Reschedule is shown as a row quick action — only include here if onReschedule is passed.
    const reschedule = rescheduleItem();
    const moveNext = moveToNextStatusItem();
    const reject = rejectItem();
    const abandon = abandonItem();
    const guidedCreate = preferCreateProject ? createProjectItem() : null;

    if (guidedCreate) items.push(guidedCreate);
    if (projectsFirst) items.push(projectsItem());
    pushMessageAndOpenContact();
    if (reschedule) items.push(reschedule);
    // Avoid duplicate “Créer un projet” when already guided to top
    if (moveNext && !(guidedCreate && moveNext.label === guidedCreate.label)) items.push(moveNext);
    if (!projectsFirst && projectCount > 0) items.push(projectsItem());
    if (abandon) items.push(abandon);
    if (reject) items.push(reject);
  } else {
    // Table context: prioritize workflow (ou Gérer les projets en premier si en production)
    const moveNext = moveToNextStatusItem();
    const reschedule = rescheduleItem();
    const reject = rejectItem();
    const abandon = abandonItem();
    const guidedCreate = preferCreateProject ? createProjectItem() : null;

    if (guidedCreate) items.push(guidedCreate);
    if (projectsFirst) items.push(projectsItem());
    if (moveNext && !(guidedCreate && moveNext.label === guidedCreate.label)) items.push(moveNext);
    pushMessageAndOpenContact();
    if (reschedule) items.push(reschedule);
    if (!projectsFirst && projectCount > 0) items.push(projectsItem());
    if (abandon) items.push(abandon);
    if (reject) items.push(reject);
  }

  appendEditDeleteMenuItems({
    items,
    t,
    onEdit: onEdit ? () => onEdit(lead) : undefined,
    onDelete: onDelete ? () => onDelete(lead) : undefined,
    omitFromMenu: omitEditDeleteFromMenu,
  });

  return items;
};

